package com.eladfinish.bible.contest.repository.remoteConfig

import com.eladfinish.bible.contest.data.remoteConfig.FirebaseRemoteConfigDataSource
import com.eladfinish.bible.contest.extensions.orNullFlow
import dev.gitlive.firebase.remoteconfig.FirebaseRemoteConfigValue
import io.github.aakira.napier.Napier
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.map
import kotlinx.serialization.KSerializer
import kotlinx.serialization.SerializationException
import kotlinx.serialization.json.Json
import kotlin.reflect.KClass
import kotlin.time.Duration

interface RemoteConfigRepository {

    /**
     * Starts a long-running config listener job that continues indefinitely
     * until the job or its coroutine scope is cancelled externally.
     *
     * Keep this job active as long as you want to receive live configuration updates.
     */
    suspend fun startConfigListener()

    /**
     * Sets the minimum interval (in seconds) that must pass before a new config fetch is allowed.
     *
     * This value is used by [fetchAndActivate] to determine whether it should fetch new
     * configuration data from the remote source or rely on cached values.
     *
     * @param seconds The minimum number of seconds between fetches.
     */
    suspend fun setMinimumFetchInterval(seconds: Duration)

    /**
     * Triggers a configuration fetch using the minimum fetch interval defined by [setMinimumFetchInterval],
     * and activates the fetched configuration so it's available for use.
     *
     * @return [Result] indicating whether new configuration values were successfully fetched and activated.
     */
    suspend fun fetchAndActivate(): Result<Boolean>

    /**
     * Forcefully triggers a configuration fetch by overriding the minimum fetch interval with 0,
     * ensuring the config is fetched from the remote source regardless of caching.
     *
     * The fetched configuration is then activated and made available for use.
     *
     * @return [Result] indicating whether new configuration values were successfully fetched and activated.
     */
    suspend fun forceFetchAndActivate(): Result<Boolean>

    /**
     * Asynchronously sets default configs using the given [Map].
     *
     * @param defaults [Map] of key value pairs representing Firebase Remote Config parameter
     *     keys and values.
     */
    suspend fun setDefaults(defaults: Map<String, Any?>)

    @Deprecated(message = "Use get(remoteConfig: RemoteConfig) instead")
    fun getString(key: String): String?

    // Workaround because reified types are not supported in interfaces
    @Deprecated(message = "Use get(remoteConfig: RemoteConfig) instead")
    fun <T : Any> get(remoteConfig: RemoteConfig, type: KClass<T>): T?

    fun <T> get(remoteConfig: RemoteConfig, serializer: KSerializer<T>): T?

    // Workaround because reified types are not supported in interfaces
    @Deprecated(message = "Use getFlow(remoteConfig: RemoteConfig) instead")
    fun <T : Any> getFlow(remoteConfig: RemoteConfig, type: KClass<T>): Flow<T?>

    fun <T> getFlow(remoteConfig: RemoteConfig, serializer: KSerializer<T>): Flow<T?>

    fun getAll(): Map<String, FirebaseRemoteConfigValue>
}

/**
 * Retrieves the currently fetched and activated configuration value for the given key.
 *
 * This returns the most recent value that was successfully fetched and activated,
 * or `null` if no such value is available or deserialization fails.
 *
 * @param remoteConfig The key to the remote config value.
 * @return The deserialized config value of type [T], or `null` if not available.
 */
inline fun <reified T : Any> RemoteConfigRepository.get(remoteConfig: RemoteConfig) =
    get(remoteConfig, T::class)

/**
 * Returns a [Flow] that emits configuration updates for the given key.
 *
 * The flow emits:
 * - The current activated value (if available),
 * - And any future updates triggered by [RemoteConfigRepository.fetchAndActivate],
 * [RemoteConfigRepository.forceFetchAndActivate],
 * or [RemoteConfigRepository.startConfigListener] (if it is running).
 *
 * This allows reactive handling of live config changes.
 *
 * @param remoteConfig The key to the remote config value.
 * @return A [Flow] of config values of type [T], emitting `null` if the value is unavailable or fails to deserialize.
 */
inline fun <reified T : Any> RemoteConfigRepository.getFlow(remoteConfig: RemoteConfig) =
    getFlow(remoteConfig, T::class)

class RemoteConfigRepositoryImpl(
    private val firebaseRemoteConfigDataSource: FirebaseRemoteConfigDataSource,
) : RemoteConfigRepository {

    private val json by lazy {
        Json {
            ignoreUnknownKeys = true
            explicitNulls = false
        }
    }

    /**
     * Starts a long-running config listener job that continues indefinitely
     * until the job or its coroutine scope is cancelled externally.
     *
     * Keep this job active as long as you want to receive live configuration updates.
     */
    override suspend fun startConfigListener() {
        firebaseRemoteConfigDataSource.startConfigListener()
    }

    /**
     * Sets the minimum interval (in seconds) that must pass before a new config fetch is allowed.
     *
     * This value is used by [fetchAndActivate] to determine whether it should fetch new
     * configuration data from the remote source or rely on cached values.
     *
     * @param seconds The minimum number of seconds between fetches.
     */
    override suspend fun setMinimumFetchInterval(seconds: Duration) {
        firebaseRemoteConfigDataSource.setMinimumFetchInterval(seconds = seconds)
    }

    /**
     * Triggers a configuration fetch using the minimum fetch interval defined by [setMinimumFetchInterval],
     * and activates the fetched configuration so it's available for use.
     *
     * @return [Result] indicating whether new configuration values were successfully fetched and activated.
     */
    override suspend fun fetchAndActivate(): Result<Boolean> {
        return firebaseRemoteConfigDataSource.fetchAndActivate(force = false)
    }

    /**
     * Forcefully triggers a configuration fetch by overriding the minimum fetch interval with 0,
     * ensuring the config is fetched from the remote source regardless of caching.
     *
     * The fetched configuration is then activated and made available for use.
     *
     * @return [Result] indicating whether new configuration values were successfully fetched and activated.
     */
    override suspend fun forceFetchAndActivate(): Result<Boolean> {
        return firebaseRemoteConfigDataSource.fetchAndActivate(force = true)
    }

    @Deprecated("Use get(remoteConfig: RemoteConfig) instead")
    override fun getString(key: String): String? =
        get(key = key, type = String::class)

    @Deprecated("Use get(remoteConfig: RemoteConfig) instead")
    override fun <T : Any> get(remoteConfig: RemoteConfig, type: KClass<T>): T? =
        get(key = remoteConfig.key, type = type)

    override fun <T> get(remoteConfig: RemoteConfig, serializer: KSerializer<T>): T? =
        get(key = remoteConfig.key, serializer = serializer)

    @Deprecated("Use getFlow(remoteConfig: RemoteConfig) instead")
    override fun <T : Any> getFlow(remoteConfig: RemoteConfig, type: KClass<T>): Flow<T?> =
        getFlow(key = remoteConfig.key, type = type)

    override fun <T> getFlow(remoteConfig: RemoteConfig, serializer: KSerializer<T>): Flow<T?> =
        getFlow(key = remoteConfig.key, serializer = serializer)

    override fun getAll(): Map<String, FirebaseRemoteConfigValue> {
        return firebaseRemoteConfigDataSource.getAll()
    }

    override suspend fun setDefaults(defaults: Map<String, Any?>) {
        firebaseRemoteConfigDataSource.setDefaults(defaults)
    }

    private fun <T : Any> get(key: String, type: KClass<T>): T? =
        when (type) {
            Boolean::class -> firebaseRemoteConfigDataSource.getBoolean(key) as? T
            Double::class -> firebaseRemoteConfigDataSource.getDouble(key) as? T
            Int::class -> firebaseRemoteConfigDataSource.getInt(key) as? T
            Long::class -> firebaseRemoteConfigDataSource.getLong(key) as? T
            String::class -> firebaseRemoteConfigDataSource.getString(key) as? T
            else -> null
        }

    private fun <T> get(key: String, serializer: KSerializer<T>): T? =
        get(key, String::class)?.let {
            try {
                json.decodeFromString(deserializer = serializer, string = it)
            } catch (e: SerializationException) {
                Napier.e(tag = "RemoteConfigRepository.get", throwable = e, message = "key: $key, value: $it")
                null
            }
        }

    private fun <T : Any> getFlow(key: String, type: KClass<T>): Flow<T?> =
        when (type) {
            Boolean::class -> firebaseRemoteConfigDataSource.getBooleanFlow(key) as? Flow<T?>
            Double::class -> firebaseRemoteConfigDataSource.getDoubleFlow(key) as? Flow<T?>
            Int::class -> firebaseRemoteConfigDataSource.getIntFlow(key) as? Flow<T?>
            Long::class -> firebaseRemoteConfigDataSource.getLongFlow(key) as? Flow<T?>
            String::class -> firebaseRemoteConfigDataSource.getStringFlow(key) as? Flow<T?>
            else -> null
        }.orNullFlow()

    private fun <T> getFlow(key: String, serializer: KSerializer<T>): Flow<T?> =
        getFlow(key, String::class).map { value ->
            value?.let {
                try {
                    json.decodeFromString(deserializer = serializer, string = value)
                } catch (e: SerializationException) {
                    Napier.e(tag = "RemoteConfigRepository.getFlow", throwable = e, message = "key: $key, value: $value")
                    null
                }
            }
        }.distinctUntilChanged()
}
